import React, { useState, useEffect } from "react";
import DepartmentBanner from "../common/DepartmentBanner";
import TermSelector from "../common/TermSelector";
import ClassScheduleService from "../../services/Department/ClassScheduleService";
import styles from './FacultyClassSchedule.module.css';
import { FaSearch, FaSpinner, FaTable, FaTh, FaSort, FaSortUp, FaSortDown, FaUserTie, FaClock, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Pagination from '../common/Pagination';
import DepartmentService from "../../services/Department/DepartmentService";

const FacultyClassSchedule = () => {
  document.title = "Faculty Class Schedule";
  const params = new URLSearchParams(window.location.search);


  const [selectedTerm, setSelectedTerm] = useState(0);
  const [selectedTermDetails, setSelectedTermDetails] = useState({});
  const [classSchedules, setClassSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [disabledColumns, setDisabledColumns] = useState([]);
  const itemsPerPage = 20;

  // Define column types
  const COLUMN_TYPES = Object.freeze({
    INSTRUCTOR: 19,
    TIME: 20,
    DAY: 21
  });

  const SORT_KEYS = Object.freeze({
    INSTRUCTOR: 'Instructor',
    TIME: 'time'
  });

  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Check if a column is enabled
  const isColumnEnabled = (columnType) => {
    return !disabledColumns.includes(columnType);
  };

  const handleTermChangeEvent = (termId, selectedTerm) => {
    setSelectedTerm(termId);
    setSelectedTermDetails(selectedTerm);
  };

  useEffect(() => {

      // Get column configuration
      DepartmentService.GetColumnConfig(6)
        .then(response => {
          const { serviceDisabledItems } = response.data || response;
          if (serviceDisabledItems && serviceDisabledItems.trim() !== "") {
            setDisabledColumns(serviceDisabledItems.split(',').map(item => parseInt(item.trim())));
          } else {
            setDisabledColumns([]);
          }
        })
        .catch(error => {
          console.error('Error fetching column configuration:', error);
          setDisabledColumns([]);
        });
    }
  , []);

  useEffect(() => {
    if (selectedTerm) {
      fetchClassSchedules(selectedTerm);
    }
  }, [selectedTerm]);

  const fetchClassSchedules = (selectedTerm) => {
    setIsLoading(true);
    ClassScheduleService.GetByDepartmentAndTerm(selectedTerm)
      .then(response => {
        setClassSchedules(response.data);
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        console.error('Error fetching page content:', error);
      });
  };

  const filteredSchedules = classSchedules.filter(schedule =>
    schedule.Instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    schedule.schedule.some(slot => 
      DAYS.some(day => slot[day]?.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedSchedules = (schedules) => {
    if (!sortConfig.key) return schedules;

    return [...schedules].sort((a, b) => {
      let aValue = a[sortConfig.key]?.toLowerCase() || '';
      let bValue = b[sortConfig.key]?.toLowerCase() || '';
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" />
      : <FaSortDown className="ms-1" />;
  };

  const formatDaySchedule = (schedule) => {
    return DAYS.map(day => schedule[day]).filter(Boolean).join(', ');
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = getSortedSchedules(filteredSchedules).slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container-fluid p-0" 
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* <DepartmentBanner /> */}
        
        <div className="container-custom py-4">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb bg-transparent p-0">
              <li className="breadcrumb-item">

              </li>
              {/* <li className="breadcrumb-item active" aria-current="page">Faculty Class Schedule</li> */}
            </ol>
          </nav>

          {/* Term Selector and Stats */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <TermSelector onTermChangeEvent={handleTermChangeEvent} />
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Faculty</p>
                  <p className="mb-0 h3">{classSchedules.length}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Current Term</p>
                  <p className="mb-0 h3">{selectedTermDetails.termWithYearLast || 'Not Selected'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-3">
                  <p className="mb-0 h3">Class Schedule</p>
                  <p className="text-muted mb-0">View faculty teaching schedules</p>
                </div>
                <div className="col-lg-9">
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="flex-grow-1">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FaSearch className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search schedules..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="btn-group view-toggle">
                      <button type="button" aria-label="Open Table"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                      >
                        <FaTable />
                      </button>
                      <button type="button" aria-label="Open Grid"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <div className="text-center py-5">
                <p className="text-muted">Loading class schedules...</p>
              </div>
            ) : viewMode === 'table' ? (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="card shadow-sm border-0"
              >
                <div className="card-body p-0">
                  <div className={styles.tableWrapper}>
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr className="bg-light">
                          {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                            <th 
                              className={`${styles.tableCell} ${styles.instructorCell} cursor-pointer`}
                              onClick={() => requestSort(SORT_KEYS.INSTRUCTOR)}
                            >
                              Instructor {getSortIcon(SORT_KEYS.INSTRUCTOR)}
                            </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.TIME) && (
                            <th className={`${styles.tableCell} ${styles.timeCell}`}>Time</th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.DAY) && DAYS.map(day => (
                            <th key={day} className={`${styles.tableCell} ${styles.dayCell}`}>
                              {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((schedule, index) => (
                            schedule.schedule.map((timeSlot, slotIndex) => (
                              <motion.tr 
                                key={`${index}-${slotIndex}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: index * 0.05 }}
                                className="align-middle"
                              >
                                {slotIndex === 0 && isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                                  <td 
                                    rowSpan={schedule.schedule.length} 
                                    className={`${styles.tableCell} ${styles.instructorCell}`}
                                  >
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-2">
                                        <FaUserTie className="text-primary" />
                                      </div>
                                      <span>{schedule.Instructor}</span>
                                    </div>
                                  </td>
                                )}
                                {isColumnEnabled(COLUMN_TYPES.TIME) && (
                                  <td className={`${styles.tableCell} ${styles.timeCell}`}>
                                    <FaClock className="me-2 text-muted" />
                                    {timeSlot.time}
                                  </td>
                                )}
                                {isColumnEnabled(COLUMN_TYPES.DAY) && DAYS.map(day => (
                                  <td key={day} className={`${styles.tableCell} ${styles.dayCell}`}>
                                    {timeSlot[day] && (
                                      <div className={styles.classInfo}>
                                        {timeSlot[day]}
                                      </div>
                                    )}
                                  </td>
                                ))}
                              </motion.tr>
                            ))
                          ))
                        ) : (
                          <tr>
                            <td colSpan={
                              (isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) ? 1 : 0) +
                              (isColumnEnabled(COLUMN_TYPES.TIME) ? 1 : 0) +
                              (isColumnEnabled(COLUMN_TYPES.DAY) ? DAYS.length : 0)
                            } className="text-center py-5">
                              <p className="text-muted mb-0">No schedules found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="row g-4"
              >
                {currentItems.map((schedule, index) => (
                  <motion.div
                    key={index}
                    className="col-md-6 col-lg-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="card h-100 border-0 shadow-sm hover-shadow">
                      <div className="card-body">
                        {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                          <div className="d-flex align-items-center mb-4">
                            <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                              <FaUserTie className="text-primary" size={24} />
                            </div>
                            <div>
                              <h5 className="card-title mb-1">{schedule.Instructor}</h5>
                              <span className="badge bg-light text-dark">
                                <FaCalendarAlt className="me-1" />
                                {selectedTermDetails.termWithYearLast}
                              </span>
                            </div>
                          </div>
                        )}
                        {schedule.schedule.map((timeSlot, slotIndex) => (
                          <div key={slotIndex} className="mb-3 pb-3 border-bottom">
                            {isColumnEnabled(COLUMN_TYPES.TIME) && (
                              <div className="d-flex align-items-center mb-2">
                                <FaClock className="text-primary me-2" />
                                <strong>{timeSlot.time}</strong>
                              </div>
                            )}
                            {isColumnEnabled(COLUMN_TYPES.DAY) && (
                              <div className="ms-4">
                                {DAYS.map(day => timeSlot[day] && (
                                  <div key={day} className="mb-1">
                                    <span className="text-capitalize fw-bold me-2">{day}:</span>
                                    <span className="badge bg-light text-dark">
                                      {timeSlot[day]}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {currentItems.length === 0 && (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted mb-0">No schedules found</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalItems={getSortedSchedules(filteredSchedules).length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default FacultyClassSchedule;